import React, { useState } from 'react'
import {
  makeStyles,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Spinner,
  Typography
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, StyleClasses } from '@paintscout/ui'
import { useClientOptions, useCrmDialogs, useUser } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import { useDealMutations } from 'shared/hooks'
import type { Deal } from '@paintscout/api'
import { useDealQuery } from '@paintscout/api'
import DealForm from 'src/components/forms/DealForm'
import { getObjectLabels, mapContactToDealContact } from '@paintscout/util/builder'
import { hasAddressMismatch } from 'src/components/dialogs/LinkQuoteDialog/hasAddressMismatch'
import classNames from 'classnames'
import { track } from 'shared/util/analytics'

const useStyles = makeStyles<Theme, DealDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {
      overflow: 'visible'
    },
    overflowVisible: {
      overflow: 'visible'
    },
    closeButton: {
      color: theme.palette.text.primary
    },
    cancel: {},
    confirm: {},
    delete: {
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.error.main
      },
      '&:active': {
        color: theme.palette.error.main
      }
    }
  }),
  { name: 'DealDialog' }
)

export interface DealDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  dealId?: string
  propsDeal?: Partial<Deal>
  onConfirm: (deal: Deal) => any
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
  onBack?: (event: React.MouseEvent<HTMLElement>) => any
  pipeline?: string
  hideProductionPipeline?: boolean
}

export function DealDialog(props: DealDialogProps) {
  const { onCancel, dealId, propsDeal, onConfirm, onBack, pipeline, hideProductionPipeline, ...baseDialogProps } = props
  const { enqueueSnackbar } = useSnackbar()
  const { saveDeal } = useDealMutations()
  const { crmContext, openDialog, dismissDialog } = useCrmDialogs()
  const classes = useStyles(props)
  const { user } = useUser()
  const { options } = useClientOptions()

  const { subtitle, contact: contactContext } = crmContext || {}
  const { hide: hideContactContext, fullContact } = contactContext || {}
  const [isLoading, setIsLoading] = useState(false)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const isCreating = !propsDeal && !dealId
  const { data: dealData, loading: dealDataLoading } = useDealQuery({
    variables: {
      id: dealId
    },
    skip: isCreating || !!propsDeal
  })
  const deal = propsDeal || dealData?.deal
  const dealPipelineKey = deal?.pipeline?.value || (deal?.pipeline as { key?: string })?.key
  const isProductionDeal = (dealPipelineKey || pipeline) === 'production-pipeline'
  const defaultEstimator = isCreating && !deal?.owner?.id ? user?.user_id ?? null : null

  const hideContact = dealId && (hideContactContext || isProductionDeal)

  // conditionally add contact to partialDeal object
  const partialDeal: Partial<Deal> = {}
  if (fullContact) {
    partialDeal.contact = mapContactToDealContact(fullContact)
    partialDeal.jobAddress = {
      address: fullContact.address ?? '',
      city: fullContact.city ?? '',
      province: fullContact.province ?? '',
      postal: fullContact.postal ?? ''
    }
    partialDeal.lead_source = fullContact.lead_source ?? ''
  }

  const handleRequestCancel = (event?: React.MouseEvent<HTMLElement>) => {
    if (isFormDirty) {
      event?.persist?.()
      openDialog(ConfirmationDialog, {
        message: <span>You have unsaved changes. Are you sure you want to exit without saving?</span>,
        onConfirm: async () => {
          dismissDialog()
          onCancel(event as React.MouseEvent<HTMLElement>)
        },
        onCancel: dismissDialog
      })
      return
    }

    onCancel(event as React.MouseEvent<HTMLElement>)
  }

  return (
    <Dialog {...baseDialogProps} onClose={() => handleRequestCancel()} classes={{ paper: classes.overflowVisible }}>
      <DialogTitle
        subtitle={subtitle}
        loading={isLoading}
        fade={false}
        rightContent={
          <IconButton size="small" onClick={handleRequestCancel} className={classes.closeButton}>
            <ClearIcon />
          </IconButton>
        }
      >
        <Typography variant="h3">{isCreating ? 'Add' : 'Edit'} Deal</Typography>
      </DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        {dealDataLoading ? (
          <Spinner />
        ) : (
          <DealForm
            isEditing={!isCreating}
            id="deal-dialog-form"
            data-testid="deal-dialog-form"
            deal={deal || partialDeal}
            onSubmit={handleSubmit}
            disabled={isLoading}
            pipeline={pipeline}
            hideContact={hideContact}
            hideProductionPipeline={hideProductionPipeline}
            onBack={onBack}
            onCancel={handleRequestCancel}
            onDirtyChange={setIsFormDirty}
            defaultEstimator={defaultEstimator}
          />
        )}
      </DialogContent>
      <DialogActions
        leftButton={
          onBack ? (
            <Button variant="textNoPadding" onClick={onBack}>
              Back
            </Button>
          ) : null
        }
      >
        <Button
          className={classNames(classes.confirm, 'tour-deal-dialog-confirm-button')}
          type="submit"
          form="deal-dialog-form"
          variant={'contained'}
          loading={isLoading}
          data-testid="deal-dialog-confirm-button"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )

  async function handleSubmit(newDeal: Deal) {
    track(newDeal._id ? 'deal_updated' : 'deal_created', {
      dealId: newDeal._id,
      pipeline: newDeal.pipeline,
      stage: newDeal.stage,
      leadSource: newDeal.lead_source
    })

    // When editing a deal whose location changed and it has linked estimates/invoices, offer to
    // push the new address onto those documents too (ENG-964). The scheduled job stays in sync
    // automatically server-side, so it's not part of this prompt.
    const linkedQuoteCount = deal?.linkedQuoteIds?.length ?? 0
    const linkedInvoiceCount = deal?.linkedInvoiceIds?.length ?? 0
    const hasLinkedDocs = linkedQuoteCount > 0 || linkedInvoiceCount > 0
    const locationChanged = !isCreating && hasAddressMismatch(newDeal.jobAddress, deal?.jobAddress)

    if (locationChanged && hasLinkedDocs) {
      const objectLabels = getObjectLabels({ options })
      const parts: string[] = []
      if (linkedQuoteCount > 0) {
        parts.push(
          linkedQuoteCount === 1 ? objectLabels.quote.value.toLowerCase() : objectLabels.quote.plural.toLowerCase()
        )
      }
      if (linkedInvoiceCount > 0) {
        parts.push(linkedInvoiceCount === 1 ? 'invoice' : 'invoices')
      }
      const linkedLabel = parts.join(' and ')

      openDialog(ConfirmationDialog, {
        title: 'Address Mismatch',
        message: `The address listed on the deal doesn't match the job address on the linked ${linkedLabel}. Do you want to update the ${linkedLabel} to use the deal's address?`,
        yesLabel: 'Yes, Update',
        noLabel: 'No Thanks',
        onConfirm: () => {
          dismissDialog()
          performSave(newDeal, true)
        },
        onNoLabelClick: () => {
          dismissDialog()
          performSave(newDeal, false)
        }
      })
      return
    }

    performSave(newDeal, false)
  }

  async function performSave(newDeal: Deal, updateLinkedQuoteAddresses: boolean) {
    setIsLoading(true)
    try {
      const saveResult = await saveDeal({ deal: newDeal, updateLinkedQuoteAddresses })
      onConfirm(saveResult.deal)
    } catch (error) {
      Sentry.captureException(error)
      enqueueSnackbar('Error saving deal', { variant: 'error' })
    }
    setIsLoading(false)
  }
}
